import { createFileRoute } from '@tanstack/react-router'
import { requireAuth } from '@/lib/route-guards'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Pagination } from "@/components/ui/pagination"
import {
  ArrowLeft,
  FileSpreadsheet,
} from "lucide-react"
import { usePropertyListQuery, useExportPropertiesExcelMutation } from '@/hooks/use-kura-queries'
import { Skeleton } from "@/components/ui/skeleton"
import { useState, useMemo } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/kura/$kuraId/properties')({
  beforeLoad: requireAuth,
  component: KuraPropertiesPage
})

function KuraPropertiesPage() {
  const { kuraId } = Route.useParams()
  const navigate = Route.useNavigate()

  // Pagination state
  const [pageIndex, setPageIndex] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  // Fetch property list from API
  const { data: propertyData, isLoading, error } = usePropertyListQuery(kuraId, pageIndex, pageSize)

  // Excel export mutation
  const exportExcelMutation = useExportPropertiesExcelMutation()

  const handleExportExcel = async () => {
    try {
      await exportExcelMutation.mutateAsync(kuraId)
      toast.success('Excel dosyası başarıyla indirildi')
    } catch (error) {
      console.error('Excel export error:', error)
      toast.error('Excel dosyası indirilemedi')
    }
  }

  // Extract first item from items array (backend returns array with single item)
  const propertyItem = propertyData?.items?.[0]
  const properties = propertyItem?.data || []
  const columnSettings = propertyItem?.propertySettings || []
  const totalCount = propertyData?.count || 0

  // Create dynamic columns based on API response
  const columns = useMemo<ColumnDef<Record<string, string | number>>[]>(() => {
    return columnSettings.map((setting) => ({
      accessorKey: setting.columnName,
      header: setting.label,
      cell: ({ row }) => {
        const value = row.getValue(setting.columnName)
        return <div>{value !== null && value !== undefined ? String(value) : '-'}</div>
      },
    }))
  }, [columnSettings])


  // TanStack Table instance
  const table = useReactTable({
    data: properties,
    columns,
    pageCount: propertyData?.pages || 1,
    manualPagination: true,
    state: {
      pagination: { pageIndex: pageIndex - 1, pageSize },
    },
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater({ pageIndex: pageIndex - 1, pageSize })
        setPageIndex(newState.pageIndex + 1)
        setPageSize(newState.pageSize)
      } else {
        setPageIndex(updater.pageIndex + 1)
        setPageSize(updater.pageSize)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto space-y-6 p-4">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto space-y-6 p-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate({ to: '/kura/$kuraId/edit', params: { kuraId } })}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri Dön
        </Button>
        <div className="text-center p-12 border rounded-lg">
          <p className="text-red-600 font-medium">Veri yüklenirken hata oluştu</p>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'Bilinmeyen hata'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto space-y-6 p-4">
      {/* Back button */}
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate({ to: '/kura/$kuraId/edit', params: { kuraId } })}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri Dön
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h1 className="text-2xl font-bold">
          {propertyItem?.label || 'Konut Listesi'}
        </h1>
        <div className="text-sm text-gray-600">
          Toplam: <span className="font-semibold">{totalCount}</span> konut
        </div>
      </div>

      {/* Export buttons */}
      <div className="flex items-center gap-2">
        <Button
          onClick={handleExportExcel}
          disabled={exportExcelMutation.isPending || totalCount === 0}
          size="sm"
          variant="outline"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          {exportExcelMutation.isPending ? 'İndiriliyor...' : "Excel'e Aktar"}
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="whitespace-nowrap">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Henüz konut bulunmamaktadır
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <Pagination
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalPages={propertyData?.pages || 1}
      />
    </div>
  )
}
